/**
 * The drop zone shown until a model is loaded, plus the progress and error
 * surface used during conversion.
 */
import { el, mount, setVisible } from "../components/dom";

export interface UploadPanel {
  readonly root: HTMLElement;
  onFiles(handler: (files: readonly File[]) => void): void;
  showProgress(message: string, fraction: number | null): void;
  showError(message: string): void;
  hide(): void;
}

export function createUploadPanel(): UploadPanel {
  const root = el("div", "absolute inset-0 grid place-items-center p-6 pointer-events-none");

  const card = el(
    "div",
    "glass glass-hover enter pointer-events-auto w-[min(460px,92vw)] p-7 text-center",
  );
  const title = el("h1", "text-lg font-semibold tracking-tight", "Barform Cloud");
  const subtitle = el(
    "p",
    "mt-1 text-sm text-[var(--color-ink-300)]",
    "Drop an IFC model and a PDF plan set to view them.",
  );

  const input = el("input");
  input.type = "file";
  input.multiple = true;
  input.className = "hidden";
  // Deliberately no `accept`. iOS maps each entry to a UTI and silently drops
  // ones it does not know: `.ifc` is unknown, so an accept list containing it
  // plus application/pdf leaves the picker showing PDFs only, making IFC
  // upload impossible on iPhone. We identify files by header anyway
  // (security/file-validation.ts), so the attribute bought nothing.

  const choose = el("button", "btn mt-5", "Choose files");
  choose.type = "button";

  const status = el("div", "mt-4 flex items-center justify-center gap-2 text-xs");
  setVisible(status, false);
  const spinner = el("span", "spinner");
  const statusText = el("span", "text-[var(--color-ink-300)]");
  mount(status, spinner, statusText);

  const error = el("p", "mt-3 text-xs text-red-300");
  setVisible(error, false);

  mount(card, title, subtitle, choose, input, status, error);
  mount(root, card);

  let handler: ((files: readonly File[]) => void) | null = null;

  function emit(list: FileList | null): void {
    if (list === null || handler === null) return;
    handler([...list]);
  }

  choose.addEventListener("click", () => {
    input.click();
  });
  input.addEventListener("change", () => {
    emit(input.files);
  });

  // Drag and drop across the whole overlay, not just the card.
  root.classList.add("pointer-events-auto");
  for (const name of ["dragenter", "dragover"] as const) {
    root.addEventListener(name, (event) => {
      event.preventDefault();
      card.classList.add("ring-1", "ring-[var(--color-accent-400)]");
    });
  }
  for (const name of ["dragleave", "drop"] as const) {
    root.addEventListener(name, (event) => {
      event.preventDefault();
      card.classList.remove("ring-1", "ring-[var(--color-accent-400)]");
    });
  }
  root.addEventListener("drop", (event) => {
    emit(event.dataTransfer?.files ?? null);
  });

  return {
    root,
    onFiles(next) {
      handler = next;
    },
    showProgress(message, fraction) {
      setVisible(error, false);
      setVisible(status, true);
      statusText.textContent =
        fraction === null ? message : `${message} ${String(Math.round(fraction * 100))}%`;
    },
    showError(message) {
      setVisible(status, false);
      setVisible(error, true);
      error.textContent = message;
    },
    hide() {
      setVisible(root, false);
      root.classList.remove("pointer-events-auto");
    },
  };
}
